import React from "react";

/**
 * PhotonicHero — layered animated SVG waves + grid, designed to be swappable
 * with a Three.js canvas later. Contains a `data-three-mount` div that a
 * Three.js renderer can attach to when ready.
 */
export default function PhotonicHero() {
    return (
        <div
            className="absolute inset-0 overflow-hidden pointer-events-none"
            data-testid="photonic-hero"
            data-three-mount
        >
            {/* Radial glow */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(900px circle at 70% 30%, rgba(124,140,255,0.10), transparent 60%), radial-gradient(700px circle at 20% 80%, rgba(180,166,255,0.07), transparent 60%)",
                }}
            />

            {/* Technical grid */}
            <div className="absolute inset-0 photonic-grid opacity-50" />

            {/* SVG interference waves */}
            <svg
                className="absolute inset-x-0 bottom-0 w-full h-[70%]"
                viewBox="0 0 1600 600"
                preserveAspectRatio="none"
                aria-hidden
            >
                <defs>
                    <linearGradient id="w1" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#7c8cff" stopOpacity="0.9" />
                        <stop offset="100%" stopColor="#7c8cff" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="w2" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#cdd4e0" stopOpacity="0.75" />
                        <stop offset="100%" stopColor="#cdd4e0" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="w3" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#b4a6ff" stopOpacity="0.55" />
                        <stop offset="100%" stopColor="#b4a6ff" stopOpacity="0" />
                    </linearGradient>
                </defs>

                {/* base baselines */}
                <g stroke="rgba(255,255,255,0.06)" strokeWidth="1">
                    {Array.from({ length: 10 }).map((_, i) => (
                        <line key={i} x1="0" y1={60 * i + 40} x2="1600" y2={60 * i + 40} />
                    ))}
                </g>

                {/* wave 1 — slow */}
                <path
                    d="M0,380 C200,280 400,480 800,360 C1200,240 1400,440 1600,360 L1600,600 L0,600 Z"
                    fill="url(#w1)"
                    opacity="0.22"
                >
                    <animate
                        attributeName="d"
                        dur="12s"
                        repeatCount="indefinite"
                        values="
                            M0,380 C200,280 400,480 800,360 C1200,240 1400,440 1600,360 L1600,600 L0,600 Z;
                            M0,360 C200,460 400,260 800,400 C1200,540 1400,300 1600,380 L1600,600 L0,600 Z;
                            M0,380 C200,280 400,480 800,360 C1200,240 1400,440 1600,360 L1600,600 L0,600 Z"
                    />
                </path>

                {/* wave 2 */}
                <path
                    d="M0,440 C300,340 500,540 900,420 C1300,300 1500,500 1600,430 L1600,600 L0,600 Z"
                    fill="url(#w2)"
                    opacity="0.18"
                >
                    <animate
                        attributeName="d"
                        dur="16s"
                        repeatCount="indefinite"
                        values="
                            M0,440 C300,340 500,540 900,420 C1300,300 1500,500 1600,430 L1600,600 L0,600 Z;
                            M0,460 C300,560 500,360 900,480 C1300,580 1500,360 1600,450 L1600,600 L0,600 Z;
                            M0,440 C300,340 500,540 900,420 C1300,300 1500,500 1600,430 L1600,600 L0,600 Z"
                    />
                </path>

                {/* wave 3 — fast thin line */}
                <path
                    d="M0,300 C200,220 400,380 800,260 C1200,140 1400,340 1600,280"
                    stroke="#b4a6ff"
                    strokeWidth="1.2"
                    fill="none"
                    opacity="0.45"
                    strokeDasharray="6 10"
                >
                    <animate
                        attributeName="stroke-dashoffset"
                        from="0"
                        to="-120"
                        dur="5s"
                        repeatCount="indefinite"
                    />
                </path>

                {/* Photon ticks */}
                {Array.from({ length: 60 }).map((_, i) => (
                    <circle
                        key={i}
                        cx={(i * 27 + 13) % 1600}
                        cy={120 + ((i * 53) % 260)}
                        r={i % 7 === 0 ? 2 : 1}
                        fill={i % 5 === 0 ? "#cdd4e0" : "#7c8cff"}
                        opacity={0.4}
                    >
                        <animate
                            attributeName="opacity"
                            values="0.1;0.9;0.1"
                            dur={`${3 + (i % 5)}s`}
                            repeatCount="indefinite"
                        />
                    </circle>
                ))}
            </svg>

            {/* Grain */}
            <div className="absolute inset-0 noise-overlay" />

            {/* Bottom fade to page */}
            <div
                className="absolute bottom-0 inset-x-0 h-40"
                style={{
                    background: "linear-gradient(180deg, transparent, #050510)",
                }}
            />
        </div>
    );
}
